import React, { useState, useEffect } from 'react';
import { Search, Lock, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Funcionalidad {
  id: string;
  nombre: string;
  descripcion: string | null;
  ruta: string;
  icono: string | null;
}

interface FuncionalidadRole {
  id: string;
  funcionalidad_id: string;
  role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
  editable: boolean;
  created_at: string;
  funcionalidad?: Funcionalidad;
}

export default function FuncionalidadesRoles() {
  const [funcionalidadesRoles, setFuncionalidadesRoles] = useState<FuncionalidadRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFuncionalidadesRoles();
  }, []);

  const fetchFuncionalidadesRoles = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('funcionalidades_roles')
        .select(`
          *,
          funcionalidad:funcionalidades(*)
        `)
        .order('created_at');

      if (error) throw error;
      setFuncionalidadesRoles(data || []);
    } catch (error: any) {
      console.error('Error fetching funcionalidades roles:', error);
      setError('Error al cargar los permisos de roles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePermission = async (funcionalidadRole: FuncionalidadRole) => {
    if (!funcionalidadRole.editable) {
      setError('Este permiso no se puede modificar');
      return;
    }

    try {
      const { error } = await supabase
        .from('funcionalidades_roles')
        .delete()
        .eq('id', funcionalidadRole.id);

      if (error) throw error;

      setSuccess('Permiso actualizado correctamente');
      fetchFuncionalidadesRoles();
    } catch (error: any) {
      console.error('Error updating permission:', error);
      setError(error.message);
    }
  };

  const handleAddPermission = async (funcionalidadId: string, role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar') => {
    try {
      const { error } = await supabase
        .from('funcionalidades_roles')
        .insert([{
          funcionalidad_id: funcionalidadId,
          role: role,
          editable: true
        }]);

      if (error) throw error;

      setSuccess('Permiso añadido correctamente');
      fetchFuncionalidadesRoles();
    } catch (error: any) {
      console.error('Error adding permission:', error);
      setError(error.message);
    }
  };

  const filteredFuncionalidades = funcionalidadesRoles.filter(fr =>
    fr.funcionalidad?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fr.funcionalidad?.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fr.funcionalidad?.ruta.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group permissions by functionality
  const groupedPermissions = filteredFuncionalidades.reduce((acc, curr) => {
    const funcId = curr.funcionalidad_id;
    if (!acc[funcId]) {
      acc[funcId] = {
        funcionalidad: curr.funcionalidad,
        roles: {}
      };
    }
    acc[funcId].roles[curr.role] = {
      id: curr.id,
      editable: curr.editable
    };
    return acc;
  }, {} as Record<string, {
    funcionalidad?: Funcionalidad;
    roles: Record<string, { id: string; editable: boolean; }>;
  }>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Roles y Permisos</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar funcionalidades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="pb-3 font-semibold text-gray-600">Funcionalidad</th>
                <th className="pb-3 font-semibold text-gray-600">Ruta</th>
                <th className="pb-3 font-semibold text-gray-600">Administrador</th>
                <th className="pb-3 font-semibold text-gray-600">Usuario Avanzado</th>
                <th className="pb-3 font-semibold text-gray-600">Usuario Estándar</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(groupedPermissions).map(({ funcionalidad, roles }) => (
                <tr key={funcionalidad?.id} className="border-b">
                  <td className="py-3">
                    <div className="flex items-center">
                      <Lock className="w-5 h-5 text-blue-500 mr-2" />
                      <div>
                        <div className="font-medium">{funcionalidad?.nombre}</div>
                        {funcionalidad?.descripcion && (
                          <div className="text-sm text-gray-500">{funcionalidad.descripcion}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {funcionalidad?.ruta}
                    </code>
                  </td>
                  <td className="py-3">
                    {roles['administrador'] ? (
                      <button
                        onClick={() => handleTogglePermission({
                          id: roles['administrador'].id,
                          funcionalidad_id: funcionalidad?.id || '',
                          role: 'administrador',
                          editable: roles['administrador'].editable,
                          created_at: new Date().toISOString()
                        })}
                        disabled={!roles['administrador'].editable}
                        className="text-green-500 hover:text-green-600 disabled:opacity-50"
                        title={roles['administrador'].editable ? 'Quitar permiso' : 'Permiso no editable'}
                      >
                        <CheckSquare className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddPermission(funcionalidad?.id || '', 'administrador')}
                        className="text-gray-400 hover:text-gray-500"
                        title="Añadir permiso"
                      >
                        <Square className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                  <td className="py-3">
                    {roles['usuario_avanzado'] ? (
                      <button
                        onClick={() => handleTogglePermission({
                          id: roles['usuario_avanzado'].id,
                          funcionalidad_id: funcionalidad?.id || '',
                          role: 'usuario_avanzado',
                          editable: roles['usuario_avanzado'].editable,
                          created_at: new Date().toISOString()
                        })}
                        disabled={!roles['usuario_avanzado'].editable}
                        className="text-green-500 hover:text-green-600 disabled:opacity-50"
                        title={roles['usuario_avanzado'].editable ? 'Quitar permiso' : 'Permiso no editable'}
                      >
                        <CheckSquare className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddPermission(funcionalidad?.id || '', 'usuario_avanzado')}
                        className="text-gray-400 hover:text-gray-500"
                        title="Añadir permiso"
                      >
                        <Square className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                  <td className="py-3">
                    {roles['usuario_estandar'] ? (
                      <button
                        onClick={() => handleTogglePermission({
                          id: roles['usuario_estandar'].id,
                          funcionalidad_id: funcionalidad?.id || '',
                          role: 'usuario_estandar',
                          editable: roles['usuario_estandar'].editable,
                          created_at: new Date().toISOString()
                        })}
                        disabled={!roles['usuario_estandar'].editable}
                        className="text-green-500 hover:text-green-600 disabled:opacity-50"
                        title={roles['usuario_estandar'].editable ? 'Quitar permiso' : 'Permiso no editable'}
                      >
                        <CheckSquare className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddPermission(funcionalidad?.id || '', 'usuario_estandar')}
                        className="text-gray-400 hover:text-gray-500"
                        title="Añadir permiso"
                      >
                        <Square className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {Object.keys(groupedPermissions).length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    <Lock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron funcionalidades</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}